import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tickerHeight, setTickerHeight] = useState(47);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => { setIsScrolled(window.scrollY > 50); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  useEffect(() => {
    const measure = () => {
      const ticker = document.querySelector('.results-ticker');
      if (ticker) setTickerHeight(ticker.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <>
      <style>{`
        .header {
          position: fixed;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.3s ease;
          padding: 16px 0;
          background: #1A1F3A;
        }
        .header.scrolled {
          background: #1A1F3A;
          box-shadow: 0 4px 24px rgba(0,0,0,0.4);
        }
        @media (max-width: 968px) {
          .header {
            background: #1A1F3A !important;
            padding: 10px 0;
            box-shadow: 0 2px 16px rgba(0,0,0,0.5);
          }
        }
        .header-container {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo-link { text-decoration: none; }
        .logo { display: flex; align-items: center; }
        .logo-image { height: 55px; width: auto; transition: transform 0.3s ease; }
        .logo-image:hover { transform: scale(1.05); }
        @media (max-width: 768px) { .logo-image { height: 40px; } }
        .nav { display: flex; align-items: center; gap: 28px; }
        .nav-link {
          font-family: 'Work Sans', sans-serif;
          font-size: 14px;
          font-weight: 700;
          color: #FFFFFF;
          text-decoration: none;
          transition: color 0.3s ease;
          cursor: pointer;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          position: relative;
        }
        .nav-link:hover { color: #FFC300; }
        .nav-link.active { color: #FFC300; }
        .nav-link.active::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 0; right: 0;
          height: 2px;
          background: #FFC300;
        }
        .phone-button {
          display: flex;
          align-items: center;
          font-family: 'Bebas Neue', sans-serif;
          font-size: 16px;
          letter-spacing: 1px;
          color: #FFFFFF;
          background: linear-gradient(135deg, #E31E24 0%, #FF6B35 100%);
          padding: 11px 20px;
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(227, 30, 36, 0.4);
          white-space: nowrap;
        }
        .phone-button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(227, 30, 36, 0.5); }
        .mobile-menu-button {
          display: none;
          background: none;
          border: 2px solid rgba(255,255,255,0.5);
          border-radius: 8px;
          color: #FFFFFF;
          cursor: pointer;
          padding: 6px 8px;
          min-height: auto;
          line-height: 1;
          transition: border-color 0.2s;
        }
        .mobile-menu-button:hover { border-color: #FFC300; color: #FFC300; }
        .mobile-menu { display: none; }
        @media (max-width: 968px) {
          .nav { display: none; }
          .mobile-menu-button { display: flex; align-items: center; justify-content: center; }
          .mobile-menu {
            display: block;
            position: fixed;
            left: 0; right: 0;
            background: rgba(20, 24, 48, 0.99);
            padding: 10px 16px 16px;
            box-shadow: 0 8px 30px rgba(0,0,0,0.5);
            transform: translateY(-110%);
            opacity: 0;
            transition: transform 0.3s ease, opacity 0.25s ease;
            pointer-events: none;
            border-bottom: 3px solid #FFC300;
            z-index: 998;
          }
          .mobile-menu.open {
            transform: translateY(0);
            opacity: 1;
            pointer-events: all;
          }
          .mobile-nav { display: flex; flex-direction: column; gap: 0; }
          .mobile-nav .nav-link {
            font-size: 17px;
            padding: 10px 0;
            border-bottom: 1px solid rgba(255,255,255,0.1);
            display: block;
          }
          .mobile-nav .nav-link.active::after { display: none; }
          .mobile-nav .phone-button {
            margin-top: 10px;
            justify-content: center;
            font-size: 18px;
            padding: 12px;
          }
        }
      `}</style>

      <header className={`header ${isScrolled ? 'scrolled' : ''}`} style={{ top: `${tickerHeight}px` }}>
        <div className="header-container">
          <Link to="/" className="logo-link">
            <div className="logo">
              <img src="/driven2win-logo.png" alt="Driven2WIN - High-Powered Promotions" className="logo-image" />
            </div>
          </Link>

          <nav className="nav">
            <Link to="/services" className={`nav-link ${location.pathname.includes('/services') ? 'active' : ''}`}>Services</Link>
            <Link to="/event-results" className={`nav-link ${location.pathname === '/event-results' ? 'active' : ''}`}>Results</Link>
            <Link to="/testimonials" className={`nav-link ${location.pathname === '/testimonials' ? 'active' : ''}`}>Testimonials</Link>
            <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}>About</Link>
            <Link to="/contact#contact-form" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>Contact</Link>
            <a href="tel:904-638-7200" className="phone-button">
              <Phone size={15} style={{ marginRight: '7px' }} />
              904-638-7200
            </a>
          </nav>

          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} style={{ top: `${tickerHeight + 62}px` }}>
          <div className="mobile-nav">
            {location.pathname !== '/' && (
              <Link to="/" className="nav-link">Home</Link>
            )}
            <Link to="/services" className={`nav-link ${location.pathname.includes('/services') ? 'active' : ''}`}>Services</Link>
            <Link to="/event-results" className={`nav-link ${location.pathname === '/event-results' ? 'active' : ''}`}>Results</Link>
            <Link to="/testimonials" className={`nav-link ${location.pathname === '/testimonials' ? 'active' : ''}`}>Testimonials</Link>
            <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}>About</Link>
            <Link to="/contact#contact-form" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>Contact</Link>
            <a href="tel:904-638-7200" className="phone-button">
              <Phone size={18} style={{ marginRight: '8px' }} />
              904-638-7200
            </a>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
